/**
 * CommandPalette — 글로벌 검색 (Sprint 23)
 *
 * 트리거: ⌘K (Mac) / Ctrl+K (Win/Linux)
 * 검색 대상:
 *   1. 메뉴 (HR + ERP 정적 인덱스, 즉시 매칭)
 *   2. 직원 (debounce 250ms, /api/hr/employees?q=)
 *   3. 공지사항 (extras 캐시 클라이언트 필터)
 * 단축키:
 *   ↑↓ 탐색, Enter 선택, ESC 닫기
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { menuSections } from '../../constants/menuData';
import { hrMenuSections } from '../../constants/hrMenuData';
import { hrEmployeeApi } from '../../services/hr/employeeApi';
// hrExtrasApi import 는 47-G 정리 후 미사용 — 공지사항 검색 제거됨
// import { hrExtrasApi, Announcement } from '../../services/hr/extrasApi';
import { useSystemMode } from '../../context/SystemModeContext';
import wikiApi from '../../services/wikiApi';

const TEXT = 'var(--ws-ink)';
const TEXT_SUB = 'var(--ws-ink-3)';
const TEXT_MUTED = 'var(--ws-ink-4)';
const BORDER = 'var(--ws-line)';
const WARM_BG = 'var(--ws-subtle)';

interface SearchHit {
  group: 'menu' | 'employee' | 'announcement' | 'wiki';
  groupLabel: string;
  id: string;
  title: string;
  subtitle?: string;
  icon: string;
  path: string;
}

// ─── 정적 메뉴 인덱스 ───
function buildMenuIndex(): SearchHit[] {
  const hits: SearchHit[] = [];
  // HR
  hrMenuSections.forEach((entry) => {
    if (entry.type === 'item') {
      hits.push({
        group: 'menu', groupLabel: 'HR 메뉴', id: `hr-${entry.key}`,
        title: entry.title, subtitle: '', icon: entry.icon, path: entry.path,
      });
    } else {
      entry.children.forEach((c) => {
        hits.push({
          group: 'menu', groupLabel: 'HR 메뉴', id: `hr-${c.key}`,
          title: c.title, subtitle: entry.title, icon: entry.icon, path: c.path,
        });
      });
    }
  });
  // ERP
  menuSections.forEach((s) => {
    s.menus.forEach((m) => {
      hits.push({
        group: 'menu', groupLabel: 'ERP 메뉴', id: `erp-${m.key}`,
        title: m.title, subtitle: s.sidebarTitle, icon: '◇', path: m.path,
      });
    });
  });
  return hits;
}

function fuzzyMatch(text: string, query: string): boolean {
  if (!query) return true;
  const t = text.toLowerCase();
  const q = query.toLowerCase();
  if (t.includes(q)) return true;
  // 초성/공백 제거 매칭 단순화 — 공백 제거 후 포함
  return t.replace(/\s+/g, '').includes(q.replace(/\s+/g, ''));
}

interface Props {
  open: boolean;
  onClose: () => void;
}

const CommandPalette: React.FC<Props> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { mode } = useSystemMode();
  const [query, setQuery] = useState('');
  const [empResults, setEmpResults] = useState<SearchHit[]>([]);
  const [wikiResults, setWikiResults] = useState<SearchHit[]>([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const menuIndex = useMemo(() => buildMenuIndex(), []);
  // HR 모드면 HR 메뉴 우선, ERP 모드면 ERP 메뉴 우선
  const sortedMenuIndex = useMemo(() => {
    if (mode === 'hr') {
      return [...menuIndex].sort((a, b) => {
        const aHr = a.groupLabel === 'HR 메뉴' ? 0 : 1;
        const bHr = b.groupLabel === 'HR 메뉴' ? 0 : 1;
        return aHr - bHr;
      });
    }
    return menuIndex;
  }, [menuIndex, mode]);

  // 메뉴 매칭
  const menuHits = useMemo(() => {
    return sortedMenuIndex
      .filter((h) => fuzzyMatch(`${h.title} ${h.subtitle || ''}`, query))
      .slice(0, 8);
  }, [sortedMenuIndex, query]);

  // 직원 검색 (debounce)
  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (q.length < 1) { setEmpResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await hrEmployeeApi.list({ q, limit: 6 });
        const list = (res.rows || []).map((u) => ({
          group: 'employee' as const,
          groupLabel: '구성원',
          id: `emp-${u.userId}`,
          title: u.name || '—',
          subtitle: [u.department, u.position, u.email].filter(Boolean).join(' · '),
          icon: '☺',
          path: `/hr/employees/${u.userId}`,
        }));
        setEmpResults(list);
      } catch { setEmpResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [query, open]);

  // 업무 위키 검색 (debounce)
  //
  // ⛔ «위키에 들어가야 볼 수 있다» 는 것이 기존 지식문서가 방치된 이유 중 하나였다.
  //    어디서든 ⌘K 로 닿게 하는 것이 이 그룹의 목적이다.
  // ⛔ 이동 주소(path)가 없는 결과는 넣지 않는다 — 눌러도 아무 일이 안 일어나면
  //    «고장» 으로 읽힌다. 주소를 만들 수 있는 것만 보여준다.
  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (q.length < 2) { setWikiResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await wikiApi.search(q, 6);
        const hits: SearchHit[] = [];
        for (const b of res.blocks) {
          if (!b.path) continue;
          hits.push({
            group: 'wiki',
            groupLabel: '업무 위키',
            id: `wiki-block-${b.id}`,
            title: b.content_plain.slice(0, 60),
            subtitle: [b.page?.title, b.status === 'published' ? '승인됨' : '검토 전']
              .filter(Boolean).join(' · '),
            icon: '◆',
            path: b.path,
          });
        }
        setWikiResults(hits.slice(0, 6));
      } catch { setWikiResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [query, open]);

  // 공지사항 검색은 47-G 정리에서 제거됨

  const allHits = useMemo(
    () => [...menuHits, ...wikiResults, ...empResults],
    [menuHits, wikiResults, empResults]
  );

  // ESC / 화살표 / Enter
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIdx((i) => Math.min(i + 1, Math.max(0, allHits.length - 1)));
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIdx((i) => Math.max(0, i - 1));
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const hit = allHits[activeIdx];
        if (hit) { navigate(hit.path); onClose(); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, allHits, activeIdx, navigate, onClose]);

  // 열릴 때 포커스 + 초기화
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // 쿼리 변경 시 활성 인덱스 리셋
  useEffect(() => { setActiveIdx(0); }, [query]);

  // 활성 항목 가시성 보장
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${activeIdx}"]`);
    if (el && 'scrollIntoView' in el) (el as HTMLElement).scrollIntoView({ block: 'nearest' });
  }, [activeIdx]);

  if (!open) return null;

  // 그룹화 렌더 — 인덱스를 절대값으로 추적
  let runningIdx = -1;
  const groups: Array<{ label: string; items: Array<{ hit: SearchHit; idx: number }> }> = [];
  const pushGroup = (label: string, hits: SearchHit[]) => {
    if (hits.length === 0) return;
    const items = hits.map((h) => { runningIdx += 1; return { hit: h, idx: runningIdx }; });
    groups.push({ label, items });
  };
  pushGroup('메뉴', menuHits);
  pushGroup('업무 위키', wikiResults);
  pushGroup('구성원', empResults);
  // pushGroup('공지', annResults); // 47-G 정리: 공지사항 검색 그룹 제거

  const isMac = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform);
  const cmdKey = isMac ? '⌘' : 'Ctrl';

  return (
    <Backdrop onClick={onClose}>
      <Panel onClick={(e) => e.stopPropagation()} role="dialog" aria-label="명령 팔레트">
        <SearchRow>
          <SearchIcon>⌕</SearchIcon>
          {/* data-bare: 테두리 없는 검색칸 — 전역 «모달 안 입력칸» 테두리·여백·초점 고리를 받지 않는다(styles/tokens.css) */}
          <Input
            data-bare
            ref={inputRef}
            placeholder="메뉴 / 구성원 / 공지 검색..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            spellCheck={false}
          />
          <KbdHint>{cmdKey}+K</KbdHint>
        </SearchRow>

        <Body ref={listRef}>
          {allHits.length === 0 ? (
            <Empty>
              {query ? `"${query}" 결과 없음` : '검색어를 입력하세요'}
              <br />
              <span style={{ fontSize: 11, color: TEXT_MUTED, marginTop: 8, display: 'inline-block' }}>
                ↑↓ 탐색 · Enter 선택 · ESC 닫기
              </span>
            </Empty>
          ) : (
            groups.map((g) => (
              <Group key={g.label}>
                <GroupLabel>{g.label}</GroupLabel>
                {g.items.map(({ hit, idx }) => (
                  <Row
                    key={hit.id}
                    data-idx={idx}
                    $active={idx === activeIdx}
                    onMouseEnter={() => setActiveIdx(idx)}
                    onClick={() => { navigate(hit.path); onClose(); }}
                  >
                    <RowIcon>{hit.icon}</RowIcon>
                    <RowText>
                      <RowTitle>{hit.title}</RowTitle>
                      {hit.subtitle && <RowSub>{hit.subtitle}</RowSub>}
                    </RowText>
                    <RowPath>{hit.path}</RowPath>
                  </Row>
                ))}
              </Group>
            ))
          )}
        </Body>

        <Footer>
          <FooterHint><Kbd>↑</Kbd><Kbd>↓</Kbd> 탐색</FooterHint>
          <FooterHint><Kbd>Enter</Kbd> 이동</FooterHint>
          <FooterHint><Kbd>ESC</Kbd> 닫기</FooterHint>
          <FooterHint style={{ marginLeft: 'auto', opacity: 0.7 }}><Kbd>?</Kbd> 단축키</FooterHint>
        </Footer>
      </Panel>
    </Backdrop>
  );
};

export default CommandPalette;

// ─── styled ───
const Backdrop = styled.div`
  position: fixed; inset: 0; background: var(--ws-overlay);
  display: flex; align-items: flex-start; justify-content: center;
  padding-top: 12vh; z-index: 2000;
`;
const Panel = styled.div`
  width: min(640px, calc(100vw - 32px));
  background: var(--ws-surface); border-radius: var(--ws-r-2xl);
  border: 1px solid var(--ws-line); box-shadow: var(--ws-shadow-lg);
  display: flex; flex-direction: column;
  overflow: hidden; max-height: 70vh;
`;
const SearchRow = styled.div`
  display: flex; align-items: center; gap: 10px;
  padding: 12px 14px; border-bottom: 1px solid ${BORDER};
`;
const SearchIcon = styled.span` font-size: 18px; color: ${TEXT_SUB}; `;
const Input = styled.input`
  flex: 1; height: 28px; border: none; outline: none; padding: 0;
  /* 테두리 없는 검색칸 — 기본 입력칸 스타일(Tailwind forms)이 초점 때 그리는 고리도 지운다 */
  box-shadow: none;
  &:focus, &:focus-visible { box-shadow: none; outline: none; }
  font-size: 14px; color: ${TEXT}; font-family: inherit; background: transparent;
  &::placeholder { color: ${TEXT_MUTED}; }
`;
const KbdHint = styled.span`
  font-size: 11px; color: ${TEXT_MUTED}; padding: 2px 6px;
  border: 1px solid ${BORDER}; border-radius: 4px; background: ${WARM_BG};
  font-family: ui-monospace, SFMono-Regular, monospace;
`;
const Body = styled.div`
  flex: 1; overflow-y: auto; padding: 6px;
`;
const Empty = styled.div`
  text-align: center; padding: 36px 16px; color: ${TEXT_SUB}; font-size: 13px;
`;
const Group = styled.div`
  margin-bottom: 6px;
`;
const GroupLabel = styled.div`
  font-size: 10.5px; font-weight: 600; color: ${TEXT_MUTED};
  padding: 8px 12px 4px; text-transform: uppercase; letter-spacing: 0.5px;
`;
const Row = styled.div<{ $active: boolean }>`
  display: flex; align-items: center; gap: 10px;
  padding: 8px 12px; border-radius: 8px; cursor: pointer;
  background: ${({ $active }) => ($active ? 'var(--ws-hover)' : 'transparent')};
  box-shadow: ${({ $active }) => ($active ? 'inset 2px 0 0 var(--ws-blue)' : 'none')};
  &:hover { background: var(--ws-hover); }
`;
const RowIcon = styled.span`
  width: 24px; height: 24px; border-radius: 6px;
  display: grid; place-items: center; background: ${WARM_BG};
  font-size: 13px; flex-shrink: 0;
`;
const RowText = styled.div` flex: 1; min-width: 0; `;
const RowTitle = styled.div`
  font-size: 13px; font-weight: 600; color: ${TEXT};
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;
const RowSub = styled.div`
  font-size: 11px; color: ${TEXT_MUTED};
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;
const RowPath = styled.span`
  font-size: 10.5px; color: ${TEXT_MUTED}; font-family: ui-monospace, monospace;
  white-space: nowrap;
`;
const Footer = styled.div`
  display: flex; gap: 14px; align-items: center;
  padding: 8px 14px; border-top: 1px solid ${BORDER};
  background: ${WARM_BG}; font-size: 11px; color: ${TEXT_SUB};
`;
const FooterHint = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
`;
const Kbd = styled.kbd`
  display: inline-block; padding: 1px 5px; border-radius: 3px;
  background: var(--ws-surface); border: 1px solid ${BORDER};
  font-family: ui-monospace, monospace; font-size: 10px; color: ${TEXT_SUB};
`;
